import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Chess } from 'chess.js';
import { useSearchParams } from 'react-router-dom';
import { API_BASE } from '../api';
import { useDataCache } from '../DataCacheContext';
import InteractiveBoard, { svgPieceUrl, playSound, getSoundType } from './InteractiveBoard';

// ── Fallback sample positions (shown when user has no missed checkmates) ──
const SAMPLE_POSITIONS = [
  { fen: '6k1/5ppp/8/8/8/8/1Q3PPP/6K1 w - - 0 1', mateDepth: 1, solutionLine: ['Qb8#'], playerColor: 'white', userActualMove: 'Qb7', opponentUsername: 'Sample', openingName: '' },
  { fen: 'r1b1k2r/ppppqppp/2n2n2/4p2Q/2B1P3/8/PPPP1PPP/RNB1K2R w KQkq - 0 1', mateDepth: 1, solutionLine: ['Qxf7#'], playerColor: 'white', userActualMove: 'Nc3', opponentUsername: 'Sample', openingName: 'Italian Game' },
  { fen: 'rnb1kbnr/pppp1ppp/8/4p3/6Pq/5P2/PPPPP2P/RNBQKBNR b KQkq - 0 1', mateDepth: 1, solutionLine: ['Qh1#'], playerColor: 'black', userActualMove: 'Qh5', opponentUsername: 'Sample', openingName: '' },
  { fen: '8/8/8/8/k7/8/8/K1R4Q w - - 0 1', mateDepth: 2, solutionLine: ['Rb1', 'Ka4', 'Qa8#'], playerColor: 'white', userActualMove: 'Qh8', opponentUsername: 'Sample', openingName: '' },
  { fen: '5rk1/ppp2ppp/8/8/8/2B5/PPP2PPP/4R1K1 w - - 0 1', mateDepth: 1, solutionLine: ['Re8#'], playerColor: 'white', userActualMove: 'Re7', opponentUsername: 'Sample', openingName: '' },
  { fen: 'r1bqk2r/ppp2Bpp/2np1n2/2b1p3/4P3/5N2/PPPP1PPP/RNBQK2R b KQkq - 0 1', mateDepth: 1, solutionLine: ['Qb6#'], playerColor: 'black', userActualMove: 'Bxf7', opponentUsername: 'Sample', openingName: '' },
  { fen: '5k2/8/8/8/8/8/8/R6R w - - 0 1', mateDepth: 3, solutionLine: ['Rh7', 'Kg8', 'Rd7', 'Kh8', 'Ra8#'], playerColor: 'white', userActualMove: 'Ra7', opponentUsername: 'Sample', openingName: '' },
  { fen: '3k4/8/8/8/8/8/R7/R5K1 w - - 0 1', mateDepth: 3, solutionLine: ['Ra7', 'Ke8', 'Rh7', 'Kf8', 'Ra8#'], playerColor: 'white', userActualMove: 'Ra4', opponentUsername: 'Sample', openingName: '' },
];

export default function FindCheckmatePanel({ c, pieceSet, boardTheme, setPieceSet, setBoardTheme }) {
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username') || '';
  const cache = useDataCache();

  // ── Data fetching ──
  const [loading, setLoading] = useState(true);
  const [positions, setPositions] = useState([]);
  const [error, setError] = useState(null);
  const [usingSamples, setUsingSamples] = useState(false);

  useEffect(() => {
    if (!username) { setLoading(false); setError('No username found'); return; }

    const cached = cache.get(`missed-checkmates:${username}`);
    if (cached) {
      if (cached.length === 0) { setPositions(SAMPLE_POSITIONS); setUsingSamples(true); }
      else { setPositions(cached); setUsingSamples(false); }
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetch(`${API_BASE}/missed-checkmates?username=${encodeURIComponent(username)}`)
      .then(r => { if (!r.ok) throw new Error(`Failed to load checkmate data (${r.status})`); return r.json(); })
      .then(data => {
        cache.set(`missed-checkmates:${username}`, data);
        if (data.length === 0) { setPositions(SAMPLE_POSITIONS); setUsingSamples(true); }
        else { setPositions(data); setUsingSamples(false); }
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [username]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Color + mate-depth filters ──
  const [colorFilter, setColorFilter] = useState('white');
  const [depthFilter, setDepthFilter] = useState(null); // null = all depths

  const filteredPositions = useMemo(() => {
    let out = colorFilter === 'all' ? positions : positions.filter(p => p.playerColor === colorFilter);
    if (depthFilter) out = out.filter(p => p.mateDepth === depthFilter);
    return out;
  }, [positions, colorFilter, depthFilter]);

  // ── Puzzle state ──
  const [posIndex, setPosIndex] = useState(0);
  const [puzzleFeedback, setPuzzleFeedback] = useState(null);
  const [hintLevel, setHintLevel] = useState(0); // 0=none, 1=piece type, 2=target square, 3=full reveal
  const [revealed, setRevealed] = useState(false); // true if hint 3 was used (don't count in stats)
  const [pStats, setPStats] = useState({ correct: 0, total: 0 });
  const [currentStep, setCurrentStep] = useState(0); // index into solutionLine for user's next move (even indices)

  // ── Board state ──
  const [chess] = useState(() => new Chess());
  const [history, setHistory] = useState([]);
  const [moveIndex, setMoveIndex] = useState(0);
  const [positionsArr, setPositionsArr] = useState([]);
  const [flipped, setFlipped] = useState(false);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [lastMove, setLastMove] = useState(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showThreats, setShowThreats] = useState(false);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragPos, setDragPos] = useState(null);
  const [dragPiece, setDragPiece] = useState(null);
  const [hoveredSquare, setHoveredSquare] = useState(null);
  const [arrows, setArrows] = useState([]);
  const [highlightedSquares, setHighlightedSquares] = useState(new Set());
  const [pendingPromotion, setPendingPromotion] = useState(null);
  const [pgnCopied, setPgnCopied] = useState(false);

  const pos = filteredPositions[posIndex] || filteredPositions[0];

  // Load puzzle position
  useEffect(() => {
    if (!pos) return;
    chess.load(pos.fen);
    setHistory([]);
    setPositionsArr([pos.fen]);
    setMoveIndex(0);
    setSelectedSquare(null);
    setLegalMoves([]);
    setLastMove(null);
    setArrows([]);
    setHighlightedSquares(new Set());
    setPendingPromotion(null);
    setCurrentStep(0);
    setHintLevel(0);
    setRevealed(false);
    // Flip board so user plays from their perspective
    setFlipped(pos.playerColor === 'black');
  }, [posIndex, colorFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentFen = positionsArr[moveIndex] || chess.fen();
  const boardChess = useMemo(() => { const bc = new Chess(); bc.load(currentFen); return bc; }, [currentFen]);

  const gameChess = useMemo(() => {
    const gc = new Chess();
    for (let i = 0; i < Math.min(moveIndex, history.length); i++) {
      try { gc.move(history[i].san); } catch { break; }
    }
    return gc;
  }, [history, moveIndex]);

  const squareName = useCallback((ri, ci) => {
    const f = flipped ? 7 - ci : ci;
    const r = flipped ? ri : 7 - ri;
    return 'abcdefgh'[f] + (r + 1);
  }, [flipped]);

  // ── Drag-and-drop ──
  const getSquareFromPoint = useCallback((clientX, clientY) => {
    const boardEl = document.querySelector('[data-board-grid]');
    if (!boardEl) return null;
    const rect = boardEl.getBoundingClientRect();
    const x = clientX - rect.left, y = clientY - rect.top;
    if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) return null;
    const col = Math.floor((x / rect.width) * 8), row = Math.floor((y / rect.height) * 8);
    const f = flipped ? 7 - col : col, r = flipped ? row : 7 - row;
    return 'abcdefgh'[f] + (r + 1);
  }, [flipped]);

  const handleDragStart = useCallback((ri, ci, e) => {
    if (puzzleFeedback) return;
    if (moveIndex !== positionsArr.length - 1) return;
    const sq = squareName(ri, ci);
    const piece = boardChess.get(sq);
    if (!piece || piece.color !== boardChess.turn()) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setDragFrom(sq); setDragPiece(piece); setDragPos({ x: clientX, y: clientY });
    setSelectedSquare(sq);
    setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to));
  }, [moveIndex, positionsArr.length, squareName, boardChess, puzzleFeedback]);

  const isPromotion = useCallback((from, to) => {
    const piece = boardChess.get(from);
    if (!piece || piece.type !== 'p') return false;
    return (piece.color === 'w' && to[1] === '8') || (piece.color === 'b' && to[1] === '1');
  }, [boardChess]);

  const executeMove = useCallback((from, to, promotion = 'q') => {
    try {
      const tempChess = new Chess(currentFen);
      const result = tempChess.move({ from, to, promotion });
      if (result) {
        const newHistory = [...history.slice(0, moveIndex), result];
        const newPositions = [...positionsArr.slice(0, moveIndex + 1), tempChess.fen()];
        chess.load(tempChess.fen());
        setHistory(newHistory);
        setPositionsArr(newPositions);
        setMoveIndex(newPositions.length - 1);
        setLastMove({ from: result.from, to: result.to });
        setSelectedSquare(null);
        setLegalMoves([]);
        playSound(getSoundType(result));

        // ── Puzzle validation ──
        if (!puzzleFeedback && !revealed) {
          const solution = pos.solutionLine;
          const expectedMove = solution[currentStep];
          const isCorrect = result.san === expectedMove;
          const isLastMove = currentStep === solution.length - 1;

          if (isCorrect) {
            if (isLastMove) {
              // Solved the full sequence!
              setPuzzleFeedback({ correct: true, movePlayed: result.san });
              setPStats(s => ({ correct: s.correct + 1, total: s.total + 1 }));
            } else {
              // More moves — auto-play opponent response after delay
              setCurrentStep(currentStep + 1); // mark as "waiting"
              setTimeout(() => {
                const opponentMove = solution[currentStep + 1];
                const autoChess = new Chess(tempChess.fen());
                const autoResult = autoChess.move(opponentMove);
                if (autoResult) {
                  const nh = [...newHistory, autoResult];
                  const np = [...newPositions, autoChess.fen()];
                  chess.load(autoChess.fen());
                  setHistory(nh);
                  setPositionsArr(np);
                  setMoveIndex(np.length - 1);
                  setLastMove({ from: autoResult.from, to: autoResult.to });
                  playSound(getSoundType(autoResult));
                  setCurrentStep(currentStep + 2); // advance past opponent move
                }
              }, 600);
            }
          } else {
            // Wrong move — show solution arrows
            setPuzzleFeedback({ correct: false, movePlayed: result.san, bestMove: expectedMove });
            setPStats(s => ({ correct: s.correct, total: s.total + 1 }));
            // Compute solution arrows from the puzzle starting position
            try {
              const arrowChess = new Chess(pos.fen);
              const solutionArrows = [];
              for (const san of solution) {
                const m = arrowChess.move(san);
                if (m) solutionArrows.push({ from: m.from, to: m.to });
                else break;
              }
              setArrows(solutionArrows);
            } catch { /* ignore */ }
          }
        }
        return true;
      }
    } catch { /* invalid */ }
    return false;
  }, [currentFen, chess, history, positionsArr, moveIndex, puzzleFeedback, pos, currentStep, revealed]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePromotionChoice = useCallback((pieceType) => {
    if (!pendingPromotion) return;
    executeMove(pendingPromotion.from, pendingPromotion.to, pieceType);
    setPendingPromotion(null);
  }, [pendingPromotion, executeMove]);

  useEffect(() => {
    if (!dragFrom) return;
    const onMove = (e) => { const cX = e.touches ? e.touches[0].clientX : e.clientX; const cY = e.touches ? e.touches[0].clientY : e.clientY; setDragPos({ x: cX, y: cY }); };
    const onEnd = (e) => {
      const cX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX; const cY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
      const dropSq = getSquareFromPoint(cX, cY);
      if (dropSq && dropSq !== dragFrom) {
        if (isPromotion(dragFrom, dropSq)) { const piece = boardChess.get(dragFrom); setPendingPromotion({ from: dragFrom, to: dropSq, color: piece.color }); setDragFrom(null); setDragPiece(null); setDragPos(null); return; }
        executeMove(dragFrom, dropSq);
      }
      setDragFrom(null); setDragPiece(null); setDragPos(null);
    };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false }); window.addEventListener('touchend', onEnd);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onEnd); window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onEnd); };
  }, [dragFrom, getSquareFromPoint, isPromotion, executeMove, boardChess]);

  const handleSquareClick = useCallback((ri, ci) => {
    if (puzzleFeedback) return;
    if (currentStep % 2 === 1) return; // waiting for auto opponent move
    if (moveIndex !== positionsArr.length - 1) return;
    const sq = squareName(ri, ci);
    const piece = boardChess.get(sq);
    if (selectedSquare) {
      const legal = boardChess.moves({ square: selectedSquare, verbose: true });
      if (legal.some(m => m.to === sq)) {
        if (isPromotion(selectedSquare, sq)) { setPendingPromotion({ from: selectedSquare, to: sq, color: boardChess.get(selectedSquare).color }); return; }
        executeMove(selectedSquare, sq); return;
      }
      if (piece && piece.color === boardChess.turn()) { setSelectedSquare(sq); setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to)); return; }
      setSelectedSquare(null); setLegalMoves([]); return;
    }
    if (piece && piece.color === boardChess.turn()) { setSelectedSquare(sq); setLegalMoves(boardChess.moves({ square: sq, verbose: true }).map(m => m.to)); }
  }, [selectedSquare, boardChess, moveIndex, positionsArr.length, squareName, isPromotion, executeMove, puzzleFeedback, currentStep]);

  const goTo = useCallback((idx) => {
    const clamped = Math.max(0, Math.min(idx, positionsArr.length - 1));
    setMoveIndex(clamped); setSelectedSquare(null); setLegalMoves([]);
    if (clamped > 0 && history[clamped - 1]) setLastMove({ from: history[clamped - 1].from, to: history[clamped - 1].to }); else setLastMove(null);
  }, [positionsArr, history]);

  // ── Derived data ──
  const turnColor = boardChess.turn();
  const fullMoveNumber = Math.floor(moveIndex / 2) + 1;
  const isCheck = boardChess.isCheck();
  const isCheckmate = boardChess.isCheckmate();
  const isStalemate = boardChess.isStalemate();
  const isThreefoldRepetition = gameChess.isThreefoldRepetition();
  const isInsufficientMaterial = boardChess.isInsufficientMaterial();
  const isFiftyMoveRule = gameChess.isDraw() && !isStalemate && !isInsufficientMaterial && !isThreefoldRepetition;
  const isDraw = isStalemate || isThreefoldRepetition || isInsufficientMaterial || isFiftyMoveRule;
  const isGameOver = isCheckmate || isDraw;
  const playerColor = flipped ? 'b' : 'w';

  const materialCount = useMemo(() => {
    const values = { p:1,n:3,b:3,r:5,q:9 };
    const pieces = { w:{p:0,n:0,b:0,r:0,q:0}, b:{p:0,n:0,b:0,r:0,q:0} };
    const start = { p:8,n:2,b:2,r:2,q:1 };
    for (const row of boardChess.board()) for (const sq of row) if (sq && sq.type !== 'k') pieces[sq.color][sq.type]++;
    const captured = { w:{}, b:{} };
    for (const t of ['q','r','b','n','p']) { captured.w[t] = start[t] - pieces.b[t]; captured.b[t] = start[t] - pieces.w[t]; }
    let wM=0,bM=0; for (const t of ['p','n','b','r','q']) { wM += pieces.w[t]*values[t]; bM += pieces.b[t]*values[t]; }
    return { pieces, captured, advantage: wM-bM };
  }, [currentFen]); // eslint-disable-line react-hooks/exhaustive-deps

  const evalPercent = useMemo(() => Math.max(5, Math.min(95, 50 + materialCount.advantage * 5)), [materialCount.advantage]);

  const heatmapData = useMemo(() => {
    if (!showHeatmap) return null;
    const map = {}; for (const f of 'abcdefgh') for (const r of '12345678') map[f+r] = 0;
    for (const sq of Object.keys(map)) { const piece = boardChess.get(sq); if (!piece || piece.color !== playerColor) continue; try { for (const m of boardChess.moves({ square: sq, verbose: true })) map[m.to]++; } catch {} }
    return map;
  }, [currentFen, showHeatmap, playerColor]); // eslint-disable-line react-hooks/exhaustive-deps

  const threatData = useMemo(() => {
    if (!showThreats) return null;
    const threats = {}; const opp = playerColor === 'w' ? 'b' : 'w';
    for (const f of 'abcdefgh') for (const r of '12345678') { const sq = f+r; const piece = boardChess.get(sq); if (!piece || piece.color !== playerColor) continue; if (boardChess.isAttacked(sq, opp)) threats[sq] = { attacked: true, defended: boardChess.isAttacked(sq, playerColor), piece }; }
    return threats;
  }, [currentFen, showThreats, playerColor]); // eslint-disable-line react-hooks/exhaustive-deps

  const generatePGN = useCallback(() => {
    const moves = history.slice(0, moveIndex).map((m, i) => (i % 2 === 0 ? `${Math.floor(i/2)+1}. ${m.san}` : m.san)).join(' ');
    const pgn = `[Event "Find Checkmate Training"]\n[Date "${new Date().toISOString().slice(0,10)}"]\n[White "?"]\n[Black "?"]\n[Result "*"]\n\n${moves} *`;
    navigator.clipboard.writeText(pgn); return pgn;
  }, [history, moveIndex]);

  const movePairs = []; for (let i = 0; i < history.length; i += 2) movePairs.push({ num: Math.floor(i/2)+1, white: history[i], black: history[i+1]||null, whiteIdx: i+1, blackIdx: i+2 });

  const nextPosition = () => {
    if (posIndex < filteredPositions.length - 1) setPosIndex(posIndex + 1);
    else setPosIndex(0);
    setPuzzleFeedback(null); setHintLevel(0); setRevealed(false); setCurrentStep(0);
  };


  const cardSx = { px:2.5, py:2, borderRadius:'16px', bgcolor:`${c.textMuted}06`, border:`1px solid ${c.textMuted}10` };
  const labelSx = { color:c.textMuted, fontSize:'0.8rem', fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', mb:1.5 };
  const accuracy = pStats.total > 0 ? Math.round((pStats.correct / pStats.total) * 100) : 0;

  // ── Mate depth counts for the left panel (color-filtered only, so clicking a depth card doesn't zero out the others) ──
  const colorFilteredPositions = useMemo(
    () => colorFilter === 'all' ? positions : positions.filter(p => p.playerColor === colorFilter),
    [positions, colorFilter]
  );
  const mateIn1Count = useMemo(() => colorFilteredPositions.filter(p => p.mateDepth === 1).length, [colorFilteredPositions]);
  const mateIn2Count = useMemo(() => colorFilteredPositions.filter(p => p.mateDepth === 2).length, [colorFilteredPositions]);
  const mateIn3Count = useMemo(() => colorFilteredPositions.filter(p => p.mateDepth === 3).length, [colorFilteredPositions]);

  // ── Loading state ──
  if (loading) {
    return (
      <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', py:12, textAlign:'center' }}>
        <Box sx={{ width:48, height:48, borderRadius:'50%', border:`3px solid ${c.accent}20`, borderTopColor:c.accent, animation:'spin 0.8s linear infinite', mb:3, '@keyframes spin': { '100%': { transform:'rotate(360deg)' } } }} />
        <Typography sx={{ color:c.textDark, fontSize:'1.2rem', fontWeight:700 }}>Analyzing your games for missed checkmates...</Typography>
        <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', mt:1 }}>This may take a moment</Typography>
      </Box>
    );
  }

  // ── Error state ──
  if (error) {
    return (
      <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', py:12, textAlign:'center' }}>
        <Typography sx={{ color:'#E4446E', fontSize:'1.2rem', fontWeight:700, mb:1 }}>Failed to load</Typography>
        <Typography sx={{ color:c.textMuted, fontSize:'1.05rem' }}>{error}</Typography>
      </Box>
    );
  }

  // ── Left panel ──
  const resetPuzzle = () => { setPosIndex(0); setPuzzleFeedback(null); setHintLevel(0); setRevealed(false); setCurrentStep(0); };
  const leftPanel = (
    <Box sx={{ minWidth:0, overflow:'auto', maxHeight:{ lg:'calc(100vh - 160px)' }, pr:{ lg:2 } }}>
      <Typography sx={{ color:c.textDark, fontSize:'1.3rem', fontWeight:800, mb:0.5 }}>Find Checkmate</Typography>
      <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', mb:3, lineHeight:1.6 }}>
        {usingSamples ? 'Practice with sample positions.' : 'Find the checkmates you missed.'}
      </Typography>

      {/* Color toggle — matches OpeningTrainer */}
      <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb:2.5 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:0.5, p:0.5, borderRadius:'50px', bgcolor:`${c.textMuted}08` }}>
          <Box onClick={() => { if (colorFilter !== 'white') { setColorFilter('white'); resetPuzzle(); } }} sx={{ width:40, height:40, borderRadius:'50%', bgcolor:'#ECE2DB', cursor:'pointer', transition:'all 0.15s ease', opacity: colorFilter === 'white' ? 1 : 0.3, transform: colorFilter === 'white' ? 'scale(1)' : 'scale(0.85)', '&:hover':{ opacity: colorFilter === 'white' ? 1 : 0.6 } }} />
          <Box onClick={() => { setColorFilter(colorFilter === 'white' ? 'black' : 'white'); resetPuzzle(); }} sx={{ cursor:'pointer', display:'flex', px:0.3, transition:'all 0.15s ease', '&:hover':{ transform:'scale(1.2)' } }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c.textMuted} strokeWidth="2" strokeLinecap="round"><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 014-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 01-4 4H3" /></svg>
          </Box>
          <Box onClick={() => { if (colorFilter !== 'black') { setColorFilter('black'); resetPuzzle(); } }} sx={{ width:40, height:40, borderRadius:'50%', bgcolor:'#111115', cursor:'pointer', transition:'all 0.15s ease', opacity: colorFilter === 'black' ? 1 : 0.3, transform: colorFilter === 'black' ? 'scale(1)' : 'scale(0.85)', '&:hover':{ opacity: colorFilter === 'black' ? 1 : 0.6 } }} />
        </Box>
        <Typography sx={{ color:c.textDark, fontSize:'1.1rem', fontWeight:700 }}>As {colorFilter}</Typography>
      </Box>

      {/* Mate depth breakdown — click to filter */}
      {colorFilteredPositions.length > 0 && (
        <Box sx={{ display:'flex', gap:1, mb:2.5 }}>
          {[{ depth:1, count:mateIn1Count, color:c.accent }, { depth:2, count:mateIn2Count, color:'#DE7345' }, { depth:3, count:mateIn3Count, color:'#E4446E' }].map(m => {
            const active = depthFilter === m.depth;
            const dim = m.count === 0;
            return (
              <Box
                key={m.depth}
                onClick={() => { if (m.count > 0) { setDepthFilter(active ? null : m.depth); resetPuzzle(); } }}
                sx={{
                  flex:1, py:1.2, borderRadius:'12px', textAlign:'center',
                  cursor: dim ? 'default' : 'pointer',
                  opacity: dim ? 0.4 : 1,
                  bgcolor: active ? `${m.color}20` : `${m.color}08`,
                  border: `1px solid ${active ? m.color : m.color + '15'}`,
                  transition:'all 0.15s ease',
                  '&:hover': dim ? {} : { bgcolor:`${m.color}18`, borderColor:`${m.color}40` },
                }}
              >
                <Typography sx={{ color:m.color, fontSize:'1.3rem', fontWeight:800 }}>{m.count}</Typography>
                <Typography sx={{ color:c.textMuted, fontSize:'0.85rem', fontWeight:600 }}>Mate in {m.depth}</Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Position list */}
      {filteredPositions.length === 0 ? (
        <Box sx={{ textAlign:'center', py:4, px:2, borderRadius:'16px', bgcolor:`${c.textMuted}06`, border:`1px solid ${c.textMuted}10` }}>
          <Typography sx={{ color:c.textDark, fontSize:'1.1rem', fontWeight:800, mb:1 }}>No positions as {colorFilter}</Typography>
          <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', lineHeight:1.7 }}>Try switching the color filter.</Typography>
        </Box>
      ) : (
        <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
          {filteredPositions.map((p, i) => {
            const isActive = posIndex === i;
            const depthColor = p.mateDepth === 1 ? c.accent : p.mateDepth === 2 ? '#DE7345' : '#E4446E';
            return (
              <Box key={i} onClick={() => { setPosIndex(i); setPuzzleFeedback(null); setHintLevel(0); setRevealed(false); setCurrentStep(0); }} sx={{
                px:2, py:1.5, borderRadius:'12px', cursor:'pointer', transition:'all 0.15s ease',
                bgcolor: isActive ? `${c.accent}10` : `${c.textMuted}06`,
                border: `1px solid ${isActive ? c.accent + '25' : c.textMuted + '10'}`,
                '&:hover':{ bgcolor:`${c.accent}08`, borderColor:`${c.accent}20` },
              }}>
                <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <Typography sx={{ color: isActive ? c.accent : c.textDark, fontSize:'1.05rem', fontWeight:700, flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {p.openingName || `Position ${i + 1}`}
                  </Typography>
                  <Box sx={{ px:1.2, py:0.3, borderRadius:'8px', ml:1, flexShrink:0, bgcolor:`${depthColor}15` }}>
                    <Typography sx={{ color:depthColor, fontSize:'0.85rem', fontWeight:800 }}>M{p.mateDepth}</Typography>
                  </Box>
                </Box>
                {p.opponentUsername && p.opponentUsername !== 'Sample' && (
                  <Typography sx={{ color:c.textMuted, fontSize:'0.88rem', mt:0.3 }}>vs {p.opponentUsername}</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );

  // ── Drill screen ──
  return (
    <Box sx={{ display:'flex', flexDirection:{ xs:'column', lg:'row' }, gap:3, alignItems:{ lg:'flex-start' } }}>

      {/* ═══ LEFT: Position list ═══ */}
      <Box sx={{ flex:{ xs:'1 1 100%', lg:'0 0 280px' }, maxWidth:{ xs:'100%', lg:280 }, minWidth:0 }}>
        {leftPanel}
      </Box>

      {/* ═══ CENTER: Board ═══ */}
      <Box sx={{ flexShrink:0, position:'relative' }}>
      <InteractiveBoard
        c={c}
        fen={currentFen}
        flipped={flipped}
        pieceSet={pieceSet}
        boardTheme={boardTheme}
        selectedSquare={selectedSquare}
        legalMoves={legalMoves}
        lastMove={lastMove}
        onSquareClick={(ri, ci) => handleSquareClick(ri, ci)}
        onDragStart={handleDragStart}
        onFlip={() => setFlipped(f => !f)}
        onGoTo={goTo}
        setArrows={setArrows}
        setHighlightedSquares={setHighlightedSquares}
        setPieceSet={setPieceSet}
        setBoardTheme={setBoardTheme}
        arrows={arrows}
        highlightedSquares={highlightedSquares}
        dragFrom={dragFrom}
        dragPos={dragPos}
        dragPiece={dragPiece}
        hoveredSquare={hoveredSquare}
        setHoveredSquare={setHoveredSquare}
        pendingPromotion={pendingPromotion}
        onPromotionChoice={handlePromotionChoice}
        setPendingPromotion={setPendingPromotion}
        positions={positionsArr}
        moveIndex={moveIndex}
        heatmapData={heatmapData}
        threatData={threatData}
        showEvalBar={true}
        evalPercent={evalPercent}
        materialAdvantage={materialCount.advantage}
      />
      </Box>

      {/* ═══ RIGHT: Info panel ═══ */}
      <Box sx={{ flex:'0 0 280px', display:'flex', flexDirection:'column', gap:2, minWidth:0 }}>

        {/* ── Puzzle context card ── */}
        {pos && (
          <Box sx={{ ...cardSx, bgcolor:`${c.accent}06`, border:`1px solid ${c.accent}12` }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:1.2, mb:1.5, flexWrap:'wrap' }}>
              <Box sx={{ px:1.5, py:0.5, borderRadius:'10px', bgcolor:`${pos.mateDepth === 1 ? c.accent : pos.mateDepth === 2 ? '#DE7345' : '#E4446E'}15`, border:`1px solid ${pos.mateDepth === 1 ? c.accent : pos.mateDepth === 2 ? '#DE7345' : '#E4446E'}30` }}>
                <Typography sx={{ color:pos.mateDepth === 1 ? c.accent : pos.mateDepth === 2 ? '#DE7345' : '#E4446E', fontSize:'0.95rem', fontWeight:800 }}>Mate in {pos.mateDepth}</Typography>
              </Box>
              {pos.openingName && (
                <Box sx={{ px:1.5, py:0.5, borderRadius:'10px', bgcolor:`${c.textMuted}08`, border:`1px solid ${c.textMuted}15` }}>
                  <Typography sx={{ color:c.textMuted, fontSize:'0.9rem', fontWeight:600 }}>{pos.openingName}</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ color:c.textDark, fontSize:'1.25rem', fontWeight:800 }}>Find the checkmate!</Typography>
            <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', lineHeight:1.7, mt:0.5 }}>
              {pos.opponentUsername && pos.opponentUsername !== 'Sample'
                ? `You had a forced checkmate against ${pos.opponentUsername} but played ${pos.userActualMove} instead.`
                : `You have a forced mate in ${pos.mateDepth}. Find the winning move${pos.mateDepth > 1 ? 's' : ''}!`}
            </Typography>
            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mt:1.5 }}>
              <Typography sx={{ color:c.textMuted, fontSize:'0.9rem', opacity:0.6 }}>Position {posIndex+1} of {filteredPositions.length}</Typography>
              {pos.gameUrl && (
                <Box component="a" href={pos.gameUrl} target="_blank" rel="noopener noreferrer" sx={{ display:'inline-flex', alignItems:'center', gap:0.5, color:c.accent, fontSize:'0.9rem', fontWeight:600, textDecoration:'none', opacity:0.75, transition:'opacity 0.15s ease', '&:hover':{ opacity:1 } }}>
                  View game
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M7 17L17 7" /><polyline points="7 7 17 7 17 17" /></svg>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* ── Multi-move step indicator ── */}
        {pos && pos.mateDepth > 1 && !puzzleFeedback && (
          <Box sx={{ ...cardSx, py:1.5 }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
              {Array.from({ length: pos.mateDepth }, (_, i) => {
                const stepIdx = i * 2; // user moves are at even indices in solutionLine
                const isCompleted = currentStep > stepIdx;
                const isCurrent = currentStep === stepIdx;
                return (
                  <React.Fragment key={i}>
                    {i > 0 && (
                      <Box sx={{ flex:1, height:2, bgcolor:`${c.textMuted}15`, borderRadius:1 }}>
                        <Box sx={{ width:isCompleted ? '100%' : '0%', height:'100%', bgcolor:c.accent, borderRadius:1, transition:'width 0.3s ease' }} />
                      </Box>
                    )}
                    <Box sx={{ width:24, height:24, borderRadius:'50%', bgcolor:isCompleted ? c.blue : isCurrent ? c.accent : `${c.textMuted}15`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <Typography sx={{ color:isCompleted || isCurrent ? '#fff' : c.textMuted, fontSize:'0.75rem', fontWeight:800 }}>{isCompleted ? '✓' : i + 1}</Typography>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        )}

        {/* ── Feedback ── */}
        {puzzleFeedback && (
          <Box sx={{ ...cardSx, bgcolor:puzzleFeedback.correct?`${c.blue}08`:'#E4446E08', border:`1px solid ${puzzleFeedback.correct?`${c.blue}25`:'#E4446E25'}` }}>
            <Typography sx={{ color:puzzleFeedback.correct?c.blue:'#E4446E', fontSize:'1.2rem', fontWeight:800, mb:0.5 }}>
              {puzzleFeedback.correct ? (isCheckmate ? 'Checkmate!' : 'Correct!') : 'Not quite'}
            </Typography>
            {!puzzleFeedback.correct && (
              <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', lineHeight:1.7 }}>
                You played <strong style={{ color:c.textDark }}>{puzzleFeedback.movePlayed}</strong>.
                The winning move was <strong style={{ color:c.blue }}>{puzzleFeedback.bestMove}</strong>.
              </Typography>
            )}
            {puzzleFeedback.correct && pos.mateDepth > 1 && (
              <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', lineHeight:1.7 }}>
                You found the full mating sequence: <strong style={{ color:c.blue }}>{pos.solutionLine.join(' ')}</strong>
              </Typography>
            )}
            <Box onClick={nextPosition} sx={{ mt:2, px:3, py:1.2, borderRadius:'12px', cursor:'pointer', bgcolor:`${c.accent}12`, border:`1.5px solid ${c.accent}30`, display:'inline-flex', alignItems:'center', gap:1, transition:'all 0.15s ease', '&:hover':{ bgcolor:`${c.accent}20` } }}>
              <Typography sx={{ color:c.accent, fontSize:'1.05rem', fontWeight:700 }}>Next Position</Typography>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={c.accent} strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
            </Box>
          </Box>
        )}

        {/* ── Progressive hints ── */}
        {!puzzleFeedback && (
          <Box sx={cardSx}>
            {hintLevel > 0 ? (
              <>
                <Typography sx={labelSx}>Hint {hintLevel} of 3</Typography>
                {hintLevel >= 1 && (() => {
                  const currentSolutionMove = pos.solutionLine[currentStep] || pos.solutionLine[0];
                  const pieceChar = currentSolutionMove.replace(/[x+#=\d]/g, '').toLowerCase()[0];
                  const pieceName = ({'q':'queen','r':'rook','b':'bishop','n':'knight','k':'king'})[pieceChar] || 'pawn';
                  return <Typography sx={{ color:c.textDark, fontSize:'1.05rem', lineHeight:1.7 }}>Look for a move with your {pieceName}.</Typography>;
                })()}
                {hintLevel >= 2 && (() => {
                  const currentSolutionMove = pos.solutionLine[currentStep] || pos.solutionLine[0];
                  // Extract target square from SAN (last 2 chars before +/#/=)
                  const cleaned = currentSolutionMove.replace(/[+#=].*$/, '');
                  const targetSquare = cleaned.slice(-2);
                  const file = targetSquare[0]; const rank = targetSquare[1];
                  return <Typography sx={{ color:c.accent, fontSize:'1.05rem', lineHeight:1.7, mt:0.5 }}>The target is on the {file}-file, rank {rank}.</Typography>;
                })()}
                {hintLevel >= 3 && (
                  <Typography sx={{ color:c.blue, fontSize:'1.1rem', fontWeight:700, mt:0.5 }}>
                    The move is: {pos.solutionLine[currentStep] || pos.solutionLine[0]}
                  </Typography>
                )}
                {hintLevel < 3 && (
                  <Box onClick={() => {
                    const next = hintLevel + 1;
                    setHintLevel(next);
                    if (next === 3) {
                      setRevealed(true);
                      // Show solution arrows on full reveal
                      try {
                        const arrowChess = new Chess(pos.fen);
                        const solutionArrows = [];
                        for (const san of pos.solutionLine) {
                          const m = arrowChess.move(san);
                          if (m) solutionArrows.push({ from: m.from, to: m.to });
                          else break;
                        }
                        setArrows(solutionArrows);
                      } catch { /* ignore */ }
                    }
                  }} sx={{ mt:1.5, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:1, opacity:0.7, '&:hover':{ opacity:1 } }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c.textMuted} strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                    <Typography sx={{ color:c.textMuted, fontSize:'0.95rem', fontWeight:600 }}>More hints ({3 - hintLevel} left)</Typography>
                  </Box>
                )}
              </>
            ) : (
              <Box onClick={() => setHintLevel(1)} sx={{ cursor:'pointer', display:'flex', alignItems:'center', gap:1.2 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={c.textMuted} strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', fontWeight:600 }}>Show hint</Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ── Session stats ── */}
        <Box sx={cardSx}>
          <Typography sx={labelSx}>Session Stats</Typography>
          <Box sx={{ display:'flex', gap:4 }}>
            <Box><Typography sx={{ color:c.textDark, fontSize:'1.8rem', fontWeight:800 }}>{pStats.total}</Typography><Typography sx={{ color:c.textMuted, fontSize:'0.9rem' }}>Attempted</Typography></Box>
            <Box><Typography sx={{ color:c.blue, fontSize:'1.8rem', fontWeight:800 }}>{pStats.correct}</Typography><Typography sx={{ color:c.textMuted, fontSize:'0.9rem' }}>Solved</Typography></Box>
            <Box><Typography sx={{ color:c.accent, fontSize:'1.8rem', fontWeight:800 }}>{accuracy}%</Typography><Typography sx={{ color:c.textMuted, fontSize:'0.9rem' }}>Accuracy</Typography></Box>
          </Box>
        </Box>

        {/* ── Turn indicator ── */}
        <Box sx={{ ...cardSx, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
            <Box sx={{ width:20, height:20, borderRadius:'50%', bgcolor:turnColor==='w'?'#ECE2DB':'#111115' }} />
            <Typography sx={{ color:c.textDark, fontSize:'1.1rem', fontWeight:700 }}>
              {isCheckmate?(turnColor==='w'?'Black wins':'White wins'):isStalemate?'Stalemate':isDraw?'Draw':isCheck?(turnColor==='w'?'White in check':'Black in check'):turnColor==='w'?'White to move':'Black to move'}
            </Typography>
          </Box>
          <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', fontWeight:600 }}>Move {fullMoveNumber}</Typography>
        </Box>

        {/* ── Captured pieces ── */}
        <Box sx={cardSx}>
          <Typography sx={labelSx}>Captured Pieces</Typography>
          <Box sx={{ display:'flex', alignItems:'center', gap:0.8, mb:0.8 }}>
            <Box sx={{ width:14, height:14, borderRadius:'50%', bgcolor:'#ECE2DB', flexShrink:0 }} />
            <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.3, minHeight:28, alignItems:'center' }}>
              {['q','r','b','n','p'].map(type => Array(materialCount.captured.w[type]).fill(null).map((_,i) => <Box key={`wc-${type}-${i}`} component="img" src={svgPieceUrl(pieceSet,`b${type.toUpperCase()}`)} alt="" sx={{ width:26, height:26, opacity:0.7 }} />))}
              {materialCount.advantage>0 && <Typography sx={{ color:c.textMuted, fontSize:'0.95rem', fontWeight:700, ml:0.5 }}>+{materialCount.advantage}</Typography>}
            </Box>
          </Box>
          <Box sx={{ display:'flex', alignItems:'center', gap:0.8 }}>
            <Box sx={{ width:14, height:14, borderRadius:'50%', bgcolor:'#111115', flexShrink:0 }} />
            <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.3, minHeight:28, alignItems:'center' }}>
              {['q','r','b','n','p'].map(type => Array(materialCount.captured.b[type]).fill(null).map((_,i) => <Box key={`bc-${type}-${i}`} component="img" src={svgPieceUrl(pieceSet,`w${type.toUpperCase()}`)} alt="" sx={{ width:26, height:26, opacity:0.7 }} />))}
              {materialCount.advantage<0 && <Typography sx={{ color:c.textMuted, fontSize:'0.95rem', fontWeight:700, ml:0.5 }}>+{Math.abs(materialCount.advantage)}</Typography>}
            </Box>
          </Box>
        </Box>

        {/* ── Move history ── */}
        <Box sx={{ ...cardSx, maxHeight:{ xs:180, md:260 }, overflow:'auto' }}>
          <Typography sx={labelSx}>Moves</Typography>
          {movePairs.length===0 ? <Typography sx={{ color:c.textMuted, fontSize:'1.05rem', fontStyle:'italic' }}>Find the checkmate...</Typography> : (
            <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.5, alignItems:'center' }}>
              {movePairs.map((pair) => (
                <React.Fragment key={pair.num}>
                  <Typography sx={{ color:c.textMuted, fontSize:'0.95rem', fontWeight:600, mr:0.2 }}>{pair.num}.</Typography>
                  <Box onClick={() => { goTo(pair.whiteIdx); playSound('move'); }} sx={{ px:1, py:0.3, borderRadius:'6px', cursor:'pointer', bgcolor:moveIndex===pair.whiteIdx?`${c.accent}25`:'transparent', '&:hover':{ bgcolor:`${c.accent}12` } }}>
                    <Typography sx={{ color:moveIndex===pair.whiteIdx?c.accent:c.textDark, fontSize:'1rem', fontWeight:moveIndex===pair.whiteIdx?700:500 }}>{pair.white.san}</Typography>
                  </Box>
                  {pair.black && <Box onClick={() => { goTo(pair.blackIdx); playSound('move'); }} sx={{ px:1, py:0.3, borderRadius:'6px', cursor:'pointer', bgcolor:moveIndex===pair.blackIdx?`${c.accent}25`:'transparent', '&:hover':{ bgcolor:`${c.accent}12` } }}>
                    <Typography sx={{ color:moveIndex===pair.blackIdx?c.accent:c.textDark, fontSize:'1rem', fontWeight:moveIndex===pair.blackIdx?700:500 }}>{pair.black.san}</Typography>
                  </Box>}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>

        {/* ── Copy PGN ── */}
        {history.length>0 && <Box onClick={() => { generatePGN(); setPgnCopied(true); setTimeout(() => setPgnCopied(false),2000); }} sx={{ ...cardSx, cursor:'pointer', display:'flex', alignItems:'center', gap:1.2, transition:'all 0.15s ease', bgcolor:pgnCopied?`${c.accent}15`:cardSx.bgcolor, border:pgnCopied?`1px solid ${c.accent}`:cardSx.border, '&:hover':{ bgcolor:`${c.textMuted}10` } }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={pgnCopied?c.accent:c.textMuted} strokeWidth="2" strokeLinecap="round">{pgnCopied?<polyline points="20 6 9 17 4 12" />:<><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></>}</svg>
          <Typography sx={{ color:pgnCopied?c.accent:c.textMuted, fontSize:'1rem', fontWeight:600 }}>{pgnCopied?'PGN Copied!':'Copy PGN'}</Typography>
        </Box>}

        {/* ── Toggles ── */}
        <Box sx={{ display:'flex', gap:1 }}>
          <Box onClick={() => setShowHeatmap(h=>!h)} sx={{ ...cardSx, flex:1, cursor:'pointer', display:'flex', alignItems:'center', gap:1, border:showHeatmap?`1px solid ${c.accent}`:cardSx.border, bgcolor:showHeatmap?`${c.accent}08`:cardSx.bgcolor, '&:hover':{ bgcolor:`${c.textMuted}10` } }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={showHeatmap?c.accent:c.textMuted} strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" opacity="0.3" /><rect x="14" y="3" width="7" height="7" opacity="0.6" /><rect x="3" y="14" width="7" height="7" opacity="0.6" /><rect x="14" y="14" width="7" height="7" /></svg>
            <Typography sx={{ color:showHeatmap?c.accent:c.textMuted, fontSize:'0.95rem', fontWeight:600 }}>Heatmap</Typography>
          </Box>
          <Box onClick={() => setShowThreats(t=>!t)} sx={{ ...cardSx, flex:1, cursor:'pointer', display:'flex', alignItems:'center', gap:1, border:showThreats?'1px solid #E4446E':cardSx.border, bgcolor:showThreats?'#E4446E08':cardSx.bgcolor, '&:hover':{ bgcolor:`${c.textMuted}10` } }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={showThreats?'#E4446E':c.textMuted} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><line x1="22" y1="12" x2="18" y2="12" /><line x1="6" y1="12" x2="2" y2="12" /><line x1="12" y1="6" x2="12" y2="2" /><line x1="12" y1="22" x2="12" y2="18" /></svg>
            <Typography sx={{ color:showThreats?'#E4446E':c.textMuted, fontSize:'0.95rem', fontWeight:600 }}>Threats</Typography>
          </Box>
        </Box>

        {/* ── Game over ── */}
        {isGameOver && <Box sx={{ ...cardSx, bgcolor:isCheckmate?`${c.accent}08`:`${c.textMuted}06`, border:isCheckmate?`1px solid ${c.accent}`:`1px solid ${c.textMuted}15` }}>
          <Typography sx={{ color:isCheckmate?c.accent:c.textDark, fontSize:'1.15rem', fontWeight:800, mb:0.3 }}>{isCheckmate?'Checkmate!':isStalemate?'Stalemate':isThreefoldRepetition?'Threefold Repetition':isInsufficientMaterial?'Insufficient Material':isFiftyMoveRule?'50-Move Rule':'Draw'}</Typography>
          <Typography sx={{ color:c.textMuted, fontSize:'1.05rem' }}>{isCheckmate?`${turnColor==='w'?'Black':'White'} wins the game.`:isStalemate?'Neither side can win — the game is drawn.':isThreefoldRepetition?'The same position occurred three times — draw.':isInsufficientMaterial?'Neither side has enough pieces to checkmate — draw.':isFiftyMoveRule?'50 moves without a capture or pawn move — draw.':'The game is drawn.'}</Typography>
        </Box>}
      </Box>
    </Box>
  );
}
